import { useState } from "react";
import { useNavigate, Navigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UserPlus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { value: "admin", label: "Admin (invite code required)" },
  { value: "enforcer", label: "Parking Enforcer (booter)" },
  { value: "runner", label: "Plate Runner (tags only)" },
  { value: "removal", label: "Removal Team" },
];

export default function Signup() {
  const { user, refresh, loading } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const inviteParam = params.get("invite") || "";
  const [form, setForm] = useState({
    email: "", full_name: "", password: "", confirm: "", role: inviteParam ? "admin" : "enforcer", phone: "",
    invite_code: inviteParam.toUpperCase(),
  });
  const [busy, setBusy] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (form.password !== form.confirm) { toast.error("Passwords don't match"); return; }
    if (form.role === "admin" && !form.invite_code.trim()) { toast.error("Admin invite code required"); return; }
    setBusy(true);
    try {
      const res = await api.post("/auth/signup", {
        email: form.email.trim().toLowerCase(),
        full_name: form.full_name.trim(),
        password: form.password,
        role: form.role,
        phone: form.phone.trim() || null,
        invite_code: form.role === "admin" ? form.invite_code.trim().toUpperCase() : null,
      });
      localStorage.setItem("pl_token", res.data.access_token);
      await refresh();
      toast.success(`Welcome, ${res.data.user.full_name}`);
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] text-white grain flex items-center justify-center p-4">
      <div className="max-w-md w-full relative z-10">
        <div className="mb-6 flex items-center gap-3">
          <div className="hazard-stripes w-3 h-12 rounded-sm" />
          <div>
            <div className="font-display text-3xl font-black tracking-tight">PARKLOCK</div>
            <div className="text-xs tracking-[0.3em] text-[#94A3B8] uppercase">Set Up Your Access</div>
          </div>
        </div>
        <form onSubmit={submit} className="glass rounded-2xl p-6 sm:p-8 space-y-4" data-testid="signup-form">
          <div>
            <h1 className="font-display text-2xl font-bold">Create your account</h1>
            <p className="text-sm text-[#94A3B8] mt-1">Pick your role and set your own credentials.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="full_name">Full name</Label>
            <Input id="full_name" required value={form.full_name}
              onChange={(e) => set("full_name", e.target.value)}
              className="bg-[#0B0F17] border-[#28364F] text-white"
              data-testid="signup-name-input" placeholder="Jane Doe" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required autoComplete="email"
              value={form.email} onChange={(e) => set("email", e.target.value)}
              className="bg-[#0B0F17] border-[#28364F] text-white"
              data-testid="signup-email-input" placeholder="you@team.com" />
          </div>

          <div className="space-y-2">
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger className="bg-[#0B0F17] border-[#28364F] text-white" data-testid="signup-role-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#131B2A] border-[#28364F] text-white">
                {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {form.role === "admin" && (
            <div className="space-y-2">
              <Label htmlFor="invite_code">Admin invite code</Label>
              <Input id="invite_code" required value={form.invite_code}
                onChange={(e) => set("invite_code", e.target.value.toUpperCase())}
                className="bg-[#0B0F17] border-amber-500/50 text-white font-mono uppercase tracking-widest"
                data-testid="signup-invite-input" placeholder="8-character code from an admin" />
              <p className="text-xs text-[#64748B]">Ask an existing admin to generate a one-time invite.</p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="phone">Phone (optional, for SMS alerts)</Label>
            <Input id="phone" value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              className="bg-[#0B0F17] border-[#28364F] text-white"
              data-testid="signup-phone-input" placeholder="+15551234567" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={6} autoComplete="new-password"
                value={form.password} onChange={(e) => set("password", e.target.value)}
                className="bg-[#0B0F17] border-[#28364F] text-white"
                data-testid="signup-password-input" placeholder="6+ characters" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirm</Label>
              <Input id="confirm" type="password" required autoComplete="new-password"
                value={form.confirm} onChange={(e) => set("confirm", e.target.value)}
                className="bg-[#0B0F17] border-[#28364F] text-white"
                data-testid="signup-confirm-input" placeholder="Repeat" />
            </div>
          </div>

          <Button type="submit" disabled={busy}
            className="w-full bg-amber-500 hover:bg-amber-400 text-[#0B0F17] font-bold h-11"
            data-testid="signup-submit-btn">
            <UserPlus className="w-4 h-4 mr-2" />
            {busy ? "Creating..." : "Create account & sign in"}
          </Button>

          <div className="text-center pt-2 border-t border-[#28364F]">
            <Link to="/login" className="text-sm text-[#94A3B8] hover:text-white inline-flex items-center gap-1" data-testid="back-to-login-link">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
